import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

export const InstallPWA = ({ isPortal = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Listen for install prompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      
      // Store the event for later use
      setDeferredPrompt(e);
      
      // Show install banner after a delay (better UX)
      setTimeout(() => {
        // Check if user hasn't dismissed it before
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (!dismissed || Date.now() - parseInt(dismissed) > 7 * 24 * 60 * 60 * 1000) {
          setShowBanner(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Detect if app was installed
    window.addEventListener('appinstalled', () => {
      console.log('✅ PWA instalada correctamente');
      setIsInstalled(true);
      setShowBanner(false);
      toast.success(isPortal 
        ? '¡App instalada! Ahora puedes acceder desde tu pantalla de inicio' 
        : '🎉 ¡App DJ instalada! Acceso rápido desde tu móvil'
      );
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isPortal]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // Si no hay evento, mostrar instrucciones manuales
      toast.info(
        <div className="space-y-2">
          <p className="font-semibold">Para instalar la app:</p>
          <ol className="text-sm space-y-1 list-decimal list-inside">
            <li>Toca el menú del navegador (⋮ o ...)</li>
            <li>Selecciona "Añadir a pantalla de inicio"</li>
            <li>Confirma la instalación</li>
          </ol>
        </div>,
        { duration: 4000 }
      );
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('✅ Usuario aceptó instalar la PWA');
      toast.success(isPortal 
        ? 'Instalando tu portal de evento...' 
        : 'Instalando app DJ...'
      );
    } else {
      console.log('❌ Usuario rechazó instalar la PWA');
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Remember dismissal for 7 days
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    toast.info('Puedes instalar la app más tarde desde el menú');
  };

  // Don't show anything if already installed
  if (isInstalled) {
    return null;
  }

  // Banner version (auto-shown after delay)
  if (showBanner) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg shadow-2xl p-4 animate-slide-up md:left-auto md:right-4 md:max-w-md">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3 pr-6">
          <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            <Smartphone className="w-6 h-6" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">
              {isPortal ? '📱 Instala tu Portal' : '🎧 Instala la App DJ'}
            </h3>
            <p className="text-sm text-white/90 mb-3">
              {isPortal 
                ? 'Accede rápido a los detalles de tu evento desde tu móvil'
                : 'Acceso rápido y funciona sin conexión en eventos'
              }
            </p>
            
            <div className="flex gap-2">
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="bg-white text-purple-600 hover:bg-white/90 font-semibold"
              >
                <Download className="w-4 h-4 mr-1" />
                Instalar
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20"
              >
                Ahora no
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Button version (always available)
  if (deferredPrompt) {
    return (
      <Button
        onClick={handleInstallClick}
        variant="outline"
        size="sm"
        className="gap-2"
      >
        <Download className="w-4 h-4" />
        {isPortal ? 'Instalar App' : 'Instalar'}
      </Button>
    );
  }

  return null;
};
